import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { MODALITY_OPTIONS } from '../../constants/observations';
import { useAppContext } from '../../contexts/AppContext';
import FacilityService from '../../services/facility.service';
import LoadingButton from '../common/LoadingButton';
import SelectField from '../common/NewSelectField';
import TextField from '../common/NewTextField';

const useStyles = makeStyles(() => ({
  buttonsContainer: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-end',
    marginTop: 24,
    width: '100%',
  },
  cancelButton: {
    borderRadius: 4,
    color: '#42526E',
    flex: '1',
    fontWeight: 500,
    textTransform: 'none',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    overflow: 'hidden',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 12,
    marginRight: '-16px',
    marginTop: 16,
    overflow: 'visible',
    paddingRight: 16,
    width: 'min(100%, 400px)',
  },
  label: {
    fontWeight: 'bold',
  },
  submitButton: {
    '&:hover': {
      backgroundColor: '#2D95C4',
    },
    backgroundColor: '#36B2EC',
    borderRadius: 4,
    color: 'white',
    flex: '1',
    fontWeight: 500,
    textTransform: 'none',
  },
  subtitle: {
    color: '#6B778C',
    fontSize: 14,
    textAlign: 'center',
  },
  title: {
    color: '#172B4D',
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    textAlign: 'center',
  },
}));

const UpdateUserForm = ({ defaultData, isSubmitting, onCancel, onSubmit, updateType, validationErrors }) => {
  const classes = useStyles();
  const roleRef = useRef();
  const facilityRef = useRef();
  const departmentRef = useRef();
  const phoneRef = useRef();
  const fteRef = useRef();

  const { currentUser } = useAppContext();

  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(defaultData?.facilityId || null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(defaultData?.departmentId || null);
  const [selectedModalities, setSelectedModalities] = useState(defaultData?.modalities || []);
  const [selectedModality, setSelectedModality] = useState(defaultData?.modality || '');
  const [selectedRole, setSelectedRole] = useState(defaultData?.role || '');
  const [fteValue, setFteValue] = useState(defaultData?.fullTimeEmployment || 1);

  useEffect(() => {
    setSelectedFacilityId(defaultData?.facilityId || null);
    setSelectedDepartmentId(defaultData?.departmentId || null);
  }, [defaultData?.facilityId, defaultData?.departmentId]);

  const facilityService = new FacilityService();

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const facilitiesData = await facilityService.getAllFacilities();

        setFacilities(facilitiesData);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    };

    fetchFacilities();
  }, []);

  useEffect(() => {
    const fetchInitialDepartments = async () => {
      if (defaultData?.facilityId) {
        try {
          const departmentsData = await facilityService.getDepartments(defaultData.facilityId);

          setDepartments(departmentsData);
        } catch (error) {
          console.error('Error fetching initial departments:', error);
          setDepartments([]);
        }
      }
    };

    fetchInitialDepartments();
  }, [defaultData?.facilityId]);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (selectedFacilityId) {
        try {
          const departmentsData = await facilityService.getDepartments(selectedFacilityId);
          setDepartments(departmentsData);
        } catch (error) {
          setDepartments([]);
        }
      } else {
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, [selectedFacilityId]);

  const getRoleOptions = () => {
    const roleOptions = [
      { label: 'Lead Tech', value: 'lead-tech' },
      { label: 'Radiologist', value: 'radiologist' },
      { label: 'Technologist', value: 'technologist' },
    ];

    if (currentUser?.role === 'admin' || currentUser?.role === 'super-admin') {
      roleOptions.push({ label: 'Admin', value: 'admin' });
    }

    return roleOptions;
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
  };

  const handleFacilityChange = (facilityId) => {
    setSelectedFacilityId(facilityId);
    setSelectedDepartmentId(null);

    if (departmentRef.current) {
      setTimeout(() => {
        departmentRef.current.setValue(null);
      }, 100);
    }
  };

  const handleDepartmentChange = (departmentId) => {
    setSelectedDepartmentId(departmentId);
  };

  const handleModalitiesChange = (modalities) => {
    setSelectedModalities(modalities);
  };

  const handleModalityChange = (modality) => {
    setSelectedModality(modality);
  };

  const getFacilityOptions = () => {
    return facilities.map((facility) => ({ label: facility.name, value: facility.id }));
  };

  const getDepartmentOptions = () => {
    return departments.map((department) => ({ label: department.name, value: department.id }));
  };

  const getFacilityRenderValue = () => {
    return facilities.find((facility) => facility.id === selectedFacilityId)?.name || '';
  };

  const getDepartmentRenderValue = () => {
    return departments.find((department) => department.id === selectedDepartmentId)?.name || '';
  };

  const getModalTitle = () => {
    switch (updateType) {
      case 'role':
        return 'Change User Role';
      case 'facility':
        return 'Change User Facility';
      case 'phone':
        return 'Phone Number';
      case 'fte':
        return 'Full Time Employment (FTE)';
      case 'modalities':
        return 'Email Modalities (Radiologists)';
      case 'modality':
        return 'Modality (Technologists)';
      default:
        return 'Update User';
    }
  };

  const onUpdateClick = () => {
    if (updateType === 'modalities') {
      onSubmit({ modalities: selectedModalities });
      return;
    }

    if (updateType === 'modality') {
      onSubmit({ modality: selectedModality });
      return;
    }

    if (updateType === 'fte') {
      const fteInput = fteRef.current?.getValue();
      fteRef.current?.clearError();

      if (!fteInput || fteInput.trim() === '') {
        fteRef.current?.setError('FTE is required');
        return;
      }

      const fteNumber = parseFloat(fteInput);
      if (Number.isNaN(fteNumber)) {
        fteRef.current?.setError('FTE must be a valid number');
        return;
      }

      if (fteNumber < 0 || fteNumber > 1) {
        fteRef.current?.setError('FTE must be between 0 and 1');
        return;
      }

      const trimmedFte = Math.round(fteNumber * 100) / 100;
      onSubmit({ fullTimeEmployment: trimmedFte });

      return;
    }

    let hasErrors = false;
    const validData = {};

    roleRef.current?.clearError();
    facilityRef.current?.clearError();
    departmentRef.current?.clearError();
    phoneRef.current?.clearError();
    fteRef.current?.clearError();

    switch (updateType) {
      case 'role': {
        if (!selectedRole) {
          roleRef.current?.setError('Role is required');
          hasErrors = true;
        } else {
          validData.role = selectedRole;
        }

        break;
      }
      case 'facility': {
        if (!selectedFacilityId) {
          facilityRef.current?.setError('Facility is required');
          hasErrors = true;
        }

        if (!selectedDepartmentId) {
          departmentRef.current?.setError('Department is required');
          hasErrors = true;
        }

        if (!hasErrors) {
          validData.facilityId = selectedFacilityId;
          validData.departmentId = selectedDepartmentId;
        }
        break;
      }

      case 'phone': {
        const phoneNumber = phoneRef.current?.getValue();

        if (!phoneNumber?.trim()) {
          phoneRef.current?.setError('Phone number is required');
          hasErrors = true;
        } else {
          validData.phoneNumber = phoneNumber.trim();
        }

        break;
      }
      default: {
        if (!selectedRole) {
          roleRef.current?.setError('Role is required');
          hasErrors = true;
        }

        if (!selectedFacilityId) {
          facilityRef.current?.setError('Facility is required');
          hasErrors = true;
        }

        if (!selectedDepartmentId) {
          departmentRef.current?.setError('Department is required');
          hasErrors = true;
        }

        if (currentUser?.role === 'admin') {
          const phoneNumber = phoneRef.current?.getValue();
          if (!phoneNumber?.trim()) {
            phoneRef.current?.setError('Phone number is required');
            hasErrors = true;
          } else {
            validData.phoneNumber = phoneNumber.trim();
          }
        }

        if (!hasErrors) {
          validData.role = selectedRole;
          validData.facilityId = selectedFacilityId;
          validData.departmentId = selectedDepartmentId;
        }
        break;
      }
    }

    if (!hasErrors) {
      onSubmit(validData);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            {getModalTitle()}
          </Typography>
        </div>

        <div className={classes.formContainer}>
          {updateType === 'modalities' && (
            <Box>
              <Typography className={classes.label}>Modalities</Typography>
              <SelectField multiple items={MODALITY_OPTIONS} value={selectedModalities} onChange={handleModalitiesChange} placeholder="Select Modalities" />
            </Box>
          )}

          {updateType === 'modality' && (
            <Box>
              <Typography className={classes.label}>Modality</Typography>
              <SelectField items={MODALITY_OPTIONS} value={selectedModality} onChange={handleModalityChange} placeholder="Select Modality" />
            </Box>
          )}

          {(updateType === 'role' || updateType === 'default') && (
            <Box>
              <Typography className={classes.label}>Role</Typography>
              <SelectField ref={roleRef} name="role" items={getRoleOptions()} placeholder="Select Role" required value={selectedRole || ''} onChange={handleRoleChange} />
            </Box>
          )}

          {(updateType === 'facility' || updateType === 'default') && (
            <Box>
              <Typography className={classes.label}>Facility</Typography>
              <SelectField
                ref={facilityRef}
                name="facilityId"
                items={getFacilityOptions()}
                placeholder="Select Facility"
                value={selectedFacilityId || ''}
                renderValue={selectedFacilityId ? getFacilityRenderValue : undefined}
                onChange={handleFacilityChange}
              />
            </Box>
          )}

          {(updateType === 'facility' || updateType === 'default') && (
            <Box>
              <Typography className={classes.label}>Department</Typography>
              <SelectField
                ref={departmentRef}
                name="departmentId"
                items={getDepartmentOptions()}
                placeholder="Select Department"
                disabled={!selectedFacilityId || departments.length === 0}
                value={selectedDepartmentId || ''}
                renderValue={selectedDepartmentId ? getDepartmentRenderValue : undefined}
                onChange={handleDepartmentChange}
              />
            </Box>
          )}

          {(updateType === 'phone' || (updateType === 'default' && currentUser?.role === 'admin')) && (
            <Box>
              <Typography className={classes.label}>Telephone</Typography>
              <TextField ref={phoneRef} name="phoneNumber" type="phone" placeholder="+27828501512" required value={defaultData?.phoneNumber || ''} />
            </Box>
          )}

          {updateType === 'fte' && (
            <Box>
              <Typography className={classes.label}>FTE</Typography>
              <TextField
                ref={fteRef}
                name="fullTimeEmployment"
                type="number"
                placeholder="1.00"
                required
                value={fteValue}
                inputProps={{
                  max: 1,
                  min: 0,
                  step: 0.01,
                }}
              />
              <Typography variant="caption" style={{ color: '#6B778C', marginTop: 4 }}>
                Enter a value between 0 and 1 (e.g., 0.65 for 65% FTE)
              </Typography>
            </Box>
          )}
        </div>

        <div className={classes.buttonsContainer}>
          <Button variant="outlined" onClick={onCancel} className={classes.cancelButton}>
            Cancel
          </Button>
          <LoadingButton variant="contained" onClick={onUpdateClick} loading={isSubmitting} disabled={isSubmitting} className={classes.submitButton}>
            Update User
          </LoadingButton>
        </div>
      </div>
    </>
  );
};

export default UpdateUserForm;
